import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tasks as tasksApi, withCloseOutOverride } from "../../lib/api";
import { useUI } from "../../lib/store";
import { notify } from "../../lib/notifications";
import type { BatchConfirmResult, Task } from "../../lib/types";
import { Button } from "../shared/Button";
import { Modal } from "../shared/Modal";
import { cn } from "../../lib/cn";

/**
 * "Confirm together". A floating bar appears when the operator has ticked one
 * or more In-Review cards; "Confirm together" opens a modal that queues the
 * whole set. The merge queue lands each one on its own (rebase on main, gate
 * again, fast-forward); one that conflicts goes back to Needs Input and the
 * rest still land.
 */
export function BatchConfirmBar({ tasks }: { tasks: Record<string, Task> }) {
  const selection = useUI((s) => s.batchSelection);
  const clear = useUI((s) => s.clearBatchSelection);
  const [open, setOpen] = useState(false);

  // Only ids still present and still in-review are actionable (a card may have
  // moved since it was ticked).
  const selected = selection
    .map((id) => tasks[id])
    .filter((t): t is Task => Boolean(t) && t.status === "in_review");

  if (selection.length === 0) return null;

  return (
    <>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
        <div
          className={cn(
            "flex items-center gap-3 bg-bg-surface rounded-full shadow-lg",
            "border border-border-default pl-4 pr-2 py-2",
          )}
        >
          <span className="text-small text-text-secondary">
            {selected.length} selected
          </span>
          <Button size="sm" onClick={() => setOpen(true)} disabled={selected.length === 0}>
            Confirm together
          </Button>
          <Button size="sm" variant="ghost" onClick={clear}>
            Clear
          </Button>
        </div>
      </div>
      {open && (
        <BatchConfirmModal
          selected={selected}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            clear();
          }}
        />
      )}
    </>
  );
}

function BatchConfirmModal({
  selected,
  onClose,
  onDone,
}: {
  selected: Task[];
  onClose: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [result, setResult] = useState<BatchConfirmResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: (ids: string[]) =>
      withCloseOutOverride((o) => tasksApi.confirmDoneBatch(ids, o)),
    onSuccess: (r) => {
      setResult(r);
      setError(null);
      qc.invalidateQueries({ queryKey: ["kanban"] });
      notifyBatchOutcome(r);
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      notify({
        severity: "error",
        title: "Confirm together failed",
        detail: msg,
      });
    },
  });

  const queued = (result?.tasks ?? []).filter((o) => o.docs_status === "queued");
  const refused = (result?.tasks ?? []).filter((o) => o.docs_status !== "queued");

  return (
    <Modal onClose={onClose} title="Confirm together">
      {!result ? (
        <div className="space-y-4">
          <p className="text-small text-text-secondary">
            These {selected.length} task{selected.length === 1 ? "" : "s"} will
            join the merge queue. Each one is rebased on main, checked again and
            lands on its own; one that conflicts goes back to Needs Input and
            the rest still land.
          </p>
          <ul className="space-y-1 max-h-[40vh] overflow-auto">
            {selected.map((t) => (
              <li
                key={t.id}
                className="text-small text-text-primary truncate px-3 py-1.5 rounded-md bg-bg-inset"
              >
                {t.title}
              </li>
            ))}
          </ul>
          {error && <p className="text-small text-status-error">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={run.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => run.mutate(selected.map((t) => t.id))}
              disabled={run.isPending}
            >
              {run.isPending ? "Queuing…" : `Confirm ${selected.length} together`}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-small text-text-secondary">
            {`Queued ${queued.length} task${queued.length === 1 ? "" : "s"} for merge`}
            {refused.length > 0 && `, ${refused.length} not queueable`}
          </p>
          {refused.length > 0 && (
            <ul className="space-y-1">
              {refused.map((o) => (
                <li
                  key={o.task_id}
                  className="text-small text-text-secondary px-3 py-1.5 rounded-md bg-bg-inset"
                >
                  <span className="text-text-primary">
                    {selected.find((t) => t.id === o.task_id)?.title ?? o.task_id}
                  </span>
                  <span className="text-text-tertiary"> — {o.message}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-end gap-2">
            <Button onClick={onDone}>Done</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Persist the batch outcome past the modal — see notifications.ts. */
function notifyBatchOutcome(r: BatchConfirmResult) {
  const queued = r.tasks.filter((o) => o.docs_status === "queued").length;
  const refused = r.tasks.length - queued;
  notify({
    severity: refused ? "warning" : "success",
    title: refused
      ? `Queued ${queued} for merge, ${refused} not queueable`
      : `Queued ${queued} for merge`,
    detail: refused ? r.message : undefined,
  });
}
